import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Typography, 
  Container,
  Card,
  CardContent,
  alpha,
  useTheme,
} from '@mui/material';

const CookiePolicyPage = () => {
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>Cookie Policy - RatePly</title>
        <meta name="description" content="RatePly Cookie Policy - How we use cookies and similar technologies on our website." />
      </Helmet>
      
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              mb: 2,
            }}
          >
            Cookie Policy
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: alpha(theme.palette.common.white, 0.9),
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
        </Box>

        {/* Content */}
        <Card
          elevation={4}
          sx={{
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Cookie Policy for RatePly
            </Typography>

            <Box sx={{ '& > *': { mb: 3 } }}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  What Are Cookies?
                </Typography>
                <Typography variant="body1" paragraph>
                  Cookies are small text files that are placed on your computer or mobile device when you visit a website. 
                  They are widely used to make websites work more efficiently and provide information to website owners.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  How We Use Cookies
                </Typography>
                <Typography variant="body1" paragraph>
                  RatePly uses cookies to enhance your experience and provide better service. We use cookies for:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Remembering your preferred currencies and settings</li>
                  <li>Understanding how you use our website to improve functionality</li>
                  <li>Ensuring the security and proper functioning of our service</li>
                  <li>Providing personalized content and advertisements (with your consent)</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Types of Cookies We Use
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Essential Cookies
                  </Typography>
                  <Typography variant="body2" paragraph>
                    These cookies are necessary for the website to function properly. They enable core functionality 
                    such as security, network management, and accessibility. You cannot opt-out of these cookies.
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Functional Cookies
                  </Typography>
                  <Typography variant="body2" paragraph>
                    These cookies enable the website to provide enhanced functionality and personalization. 
                    They may be set by us or by third-party providers whose services we have added to our pages.
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Analytics Cookies
                  </Typography>
                  <Typography variant="body2" paragraph>
                    These cookies help us understand how visitors interact with our website by collecting and 
                    reporting information anonymously. This helps us improve our website's performance.
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Marketing Cookies
                  </Typography>
                  <Typography variant="body2" paragraph>
                    These cookies are used to track visitors across websites with the intention of displaying 
                    relevant and engaging advertisements. We only use these with your explicit consent.
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Third-Party Cookies
                </Typography>
                <Typography variant="body1" paragraph>
                  We may use third-party services that place cookies on your device. These services include:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Google Analytics for website analytics</li>
                  <li>Content delivery networks for improved performance</li>
                  <li>Security services for fraud prevention</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  How to Control Cookies
                </Typography>
                <Typography variant="body1" paragraph>
                  You have several options to manage cookies:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li><strong>Browser Settings:</strong> Most browsers allow you to control cookies through their settings preferences</li>
                  <li><strong>Cookie Banner:</strong> When you first visit our site, you can choose which types of cookies to accept</li>
                  <li><strong>Opt-out Links:</strong> You can opt-out of specific third-party cookies using their provided mechanisms</li>
                  <li><strong>Do Not Track:</strong> We respect browser "Do Not Track" signals where technically feasible</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Local Storage and Session Storage
                </Typography>
                <Typography variant="body1" paragraph>
                  In addition to cookies, we may use local storage and session storage technologies to:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Store your currency preferences</li>
                  <li>Cache exchange rate data for faster loading</li>
                  <li>Remember your interface preferences</li>
                  <li>Maintain session information for security</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Cookie Retention
                </Typography>
                <Typography variant="body1" paragraph>
                  Different cookies have different retention periods:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
                  <li><strong>Persistent Cookies:</strong> Remain on your device for a set period or until you delete them</li>
                  <li><strong>Essential Cookies:</strong> Typically expire after 1 year</li>
                  <li><strong>Analytics Cookies:</strong> Usually expire after 2 years</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Updates to This Policy
                </Typography>
                <Typography variant="body1" paragraph>
                  We may update this Cookie Policy from time to time to reflect changes in technology, 
                  legislation, or our practices. We will notify you of any significant changes by 
                  posting the updated policy on this page.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Contact Us
                </Typography>
                <Typography variant="body1" paragraph>
                  If you have any questions about our use of cookies or this Cookie Policy, please contact us at:
                </Typography>
                <Typography variant="body2">
                  Email: cookies@rateply.com
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default CookiePolicyPage;
